import React, { useState, useEffect } from "react";

import {
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { BlockStandard } from "components/admin/BlockStandard";
import { DeviceType } from "api/onboardingTypes";
import { useGreenEnergyContext } from "contexts/GreenEnergyContext";
import { fetchACTypes } from "api/onboarding";
import { useToken } from "contexts/UserContext";
import { useSetError } from "contexts/ErrorContext";
import { CustomCodes } from "contexts/ErrorContextData";

const FRAME_DATA = {
  title: "Buscar melhores soluções",
  buttonText: "Continuar",
  nextScreen: "room-wifi-status",
  skipTo: "room-wifi-status",
  h2Subtitle:
    "Indique a quantidade de ambientes climatizados por tipo de aparelho",
  explanationBody:
    "Essas informações permitem calcular a quantidade ideal de dispositivos IoT para eliminar o desperdício.",
};

export const DeviceRegistry = ({ history }) => {
  const token = useToken();
  const { setDevices } = useGreenEnergyContext();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectors, setSelectors] = useState<
    { id: DeviceType["id"]; type: DeviceType["name"] | ""; amt: string }[]
  >([{ id: "first", type: "", amt: "0" }]);
  const [acOptions, setACOptions] = useState<DeviceType[]>([]);
  const setGlobalError = useSetError();

  useEffect(() => {
    const getTypesList = async () => {
      try {
        const data = await fetchACTypes(token);
        setACOptions(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    getTypesList();
  }, [token, setGlobalError]);

  const handlesubmit = () => {
    if (selectors.every((selector) => !!selector.type)) {
      const devices = {};
      selectors.forEach((selector) => {
        devices[selector.type] = selector.amt;
      });
      setDevices(devices);
      history.push(FRAME_DATA.nextScreen);
    } else {
      setGlobalError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  const handleAdd = () => {
    if (selectors.length < acOptions.length) {
      const randomId = new Date().getTime().toLocaleString();
      setSelectors((prevVal) => [
        ...prevVal,
        { id: randomId, type: "", amt: "0" },
      ]);
    }
  };

  const handleAmtChange = (id, newAmt) => {
    setSelectors((prevVal) =>
      prevVal.map((selector) =>
        selector.id === id ? { ...selector, amt: newAmt } : selector
      )
    );
  };

  const handleTypeChange = (id, newType) => {
    setSelectors((prevVal) =>
      prevVal.map((selector) =>
        selector.id === id ? { ...selector, type: newType } : selector
      )
    );
  };

  return (
    <SolutionsFrame
      {...FRAME_DATA}
      onButtonClick={handlesubmit}
      isLoading={isLoading}
    >
      <img
        src="/static/img/solutions/device-registry.png"
        alt=""
        style={{
          borderRadius: "18px",
          width: "100%",
          marginTop: "16px",
          marginBottom: "16px",
        }}
      />

      <BlockStandard hideDivider>
        <Grid item container direction="row" alignItems="center">
          <Typography
            variant="subtitle1"
            fontWeight="demibold"
            color="primary"
            sx={{ flex: 1 }}
          >
            Ares-condicionados
          </Typography>
          <Button
            variant="contained"
            aria-label="Adicionar tipo de ar condicionado"
            onClick={handleAdd}
            disabled={selectors.length >= acOptions.length}
          >
            <AddIcon />
          </Button>
        </Grid>
        {selectors.map(({ id, type, amt }) => (
          <Grid
            key={id}
            item
            container
            direction="row"
            justifyContent="space-between"
          >
            <Select
              sx={{ flex: 3, mr: 2 }}
              value={type}
              onChange={({ target }) => handleTypeChange(id, target.value)}
            >
              {acOptions.map(({ id, name }) => (
                <MenuItem value={id} key={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
            <TextField
              sx={{ flex: 1 }}
              label="Ambientes"
              value={amt}
              onChange={({ target }) => handleAmtChange(id, target.value)}
            />
          </Grid>
        ))}
      </BlockStandard>
    </SolutionsFrame>
  );
};
